import { Badge, Box, Flex, Image, Link, Text, Tooltip } from '@chakra-ui/react'
import { ExternalLink } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { getPageNumber, toWebUrl } from '../../utils/util'

interface LinkChipProps {
  href: string
  label: string
  isInline?: boolean
  title?: string
}

// Helper functions
const getDomain = (href: string): string => {
  try {
    // For mailto links, always show the full email address
    if (href.startsWith('mailto:')) {
      return href.replace('mailto:', '')
    }

    const url = new URL(toWebUrl(href))
    let domain = url.hostname.replace(/^www\./, '')
    // Remove common TLDs to keep only the brand name
    domain = domain.replace(/\.(com|org|net|edu|gov|co\.uk|co|io|ai|app)$/, '')
    return domain
  } catch {
    return href
  }
}

const getFavicon = (href: string): string => {
  try {
    const url = new URL(toWebUrl(href))
    const fullDomain = url.hostname.replace(/^www\./, '')
    return `https://icons.duckduckgo.com/ip3/${fullDomain}.ico`
  } catch {
    return `https://icons.duckduckgo.com/ip3/${href}.ico`
  }
}

const guessTypeBadge = (href: string): string | null => {
  const domain = getDomain(href)
  if (/youtube\.com|youtu\.be/.test(domain)) return 'Video'
  if (/docs\.google\.com/.test(domain)) return 'Doc'
  if (/github\.com/.test(domain)) return 'Code'
  if (/sharepoint\.com|\.sharepoint\./.test(domain)) return 'SharePoint'
  return null
}

const createValidPreviewContent = (href: string, label: string, title?: string, faviconError?: boolean) => {
  const url = new URL(toWebUrl(href))
  const fullDomain = url.hostname.replace(/^www\./, '')
  const typeBadge = guessTypeBadge(href)
  const pageNumber = getPageNumber(href)

  return (
    <Box maxW='280px'>
      {/* Header with favicon and domain */}
      <Flex align='center' gap={2} mb={3}>
        {!faviconError ? (
          <Image
            src={getFavicon(href)}
            alt=''
            w={4}
            h={4}
            borderRadius='sm'
            flexShrink={0}
            fallback={<ExternalLink size={16} color='#718096' />}
          />
        ) : (
          <ExternalLink size={16} color='#718096' />
        )}
        <Box flex={1} overflow='hidden'>
          <Text fontSize='xs' color='gray.500' fontWeight='medium' noOfLines={1}>
            {fullDomain}
          </Text>
          <Flex gap={2} align='center' mt={0.5}>
            {typeBadge && (
              <Badge size='sm' colorScheme='blue' fontSize='10px'>
                {typeBadge}
              </Badge>
            )}
            {pageNumber && (
              <Badge size='sm' colorScheme='gray' fontSize='10px'>
                Page {pageNumber}
              </Badge>
            )}
          </Flex>
        </Box>
      </Flex>

      {/* Title */}
      <Text fontSize='sm' fontWeight='semibold' mb={2} lineHeight='1.3' noOfLines={2}>
        {title || label}
      </Text>

      {/* URL */}
      <Text
        fontSize='xs'
        color='gray.400'
        fontFamily='mono'
        noOfLines={1}
        bg='gray.50'
        px={2}
        py={1}
        borderRadius='sm'
      >
        {href}
      </Text>
    </Box>
  )
}

const createFallbackPreviewContent = (href: string, label: string, title?: string) => {
  return (
    <Box maxW='280px'>
      <Flex align='center' gap={2} mb={2}>
        <ExternalLink size={16} color='#718096' />
        <Text fontSize='sm' fontWeight='semibold'>
          {title || label}
        </Text>
      </Flex>
      <Text fontSize='xs' color='gray.400' fontFamily='mono' bg='gray.50' px={2} py={1} borderRadius='sm'>
        {href}
      </Text>
    </Box>
  )
}

const createPreviewContent = (href: string, label: string, title?: string, faviconError?: boolean) => {
  try {
    return createValidPreviewContent(href, label, title, faviconError)
  } catch {
    return createFallbackPreviewContent(href, label, title)
  }
}

const LinkChip: React.FC<LinkChipProps> = ({ href, label, title, isInline = true }) => {
  const typeBadge = guessTypeBadge(href)
  const [faviconError, setFaviconError] = useState(false)
  const displayText = title || label

  const handleFaviconError = () => {
    setFaviconError(true)
  }

  const previewContent = useMemo(
    () => createPreviewContent(href, label, title, faviconError),
    [href, label, title, faviconError],
  )

  return (
    <Tooltip
      label={previewContent}
      hasArrow
      placement='top'
      bg='white'
      color='black'
      borderRadius='lg'
      boxShadow='xl'
      border='1px solid'
      borderColor='gray.200'
      p={4}
      openDelay={300}
      closeDelay={100}
    >
      <Link
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        display='inline-flex'
        alignItems='center'
        gap={1.5}
        verticalAlign='baseline'
        color='inherit'
        opacity={0.7}
        transition='all 0.15s'
        _hover={{
          opacity: 1,
        }}
        _focus={{
          outline: 'none',
          opacity: 1,
        }}
      >
        {!isInline && (
          !faviconError ? (
            <Image
              src={getFavicon(href)}
              alt=''
              w={3}
              h={3}
              borderRadius='xs'
              flexShrink={0}
              onError={handleFaviconError}
            />
          ) : (
            <ExternalLink size={12} style={{ flexShrink: 0, marginRight: '-4px' }} />
          )
        )}
        <Text
          fontSize='xs'
          fontWeight='normal'
          lineHeight='1'
          maxW='120px'
          overflow='hidden'
          textOverflow='ellipsis'
          whiteSpace='nowrap'
          flexShrink={1}
        >
          {displayText}
          {typeBadge ? ` · ${typeBadge}` : ''}
        </Text>
      </Link>
    </Tooltip>
  )
}

export default LinkChip
